import { Link } from 'react-router-dom';
import { Card, Typography, Badge } from '../components/ui';
import AnimatedText from '../components/AnimatedText';

const Home = () => {
  return (
    <div
      className="min-h-screen"
      style={{
        background: `linear-gradient(135deg, var(--bg-secondary) 0%, var(--bg-tertiary) 100%)`,
        color: 'var(--text-primary)',
      }}
    >
      {/* Hero Section */}
      <div className="container mx-auto px-4 py-8 sm:py-12 lg:py-16">
        <div className="text-center mb-12 sm:mb-16">
          <Typography variant="h1" align="center" className="mb-4 sm:mb-6">
            Smart Inventory Management
            <div style={{ color: 'var(--accent)' }}>
              <AnimatedText
                words={[
                  'Sudo IMS',
                  'Efficiency',
                  'Control',
                  'Automation',
                  'Optimization',
                ]}
                interval={2000}
              />
            </div>
          </Typography>
          <Typography
            variant="subtitle1"
            align="center"
            color="secondary"
            className="max-w-3xl mx-auto leading-relaxed mb-6 sm:mb-8"
          >
            Streamline your warehouse operations with our comprehensive
            inventory management system. From real-time stock tracking to
            automated reordering, we help you optimize your supply chain and
            reduce operational costs.
          </Typography>
          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-6 sm:mb-8">
            <Link
              to="/dashboard"
              className="w-full sm:w-auto px-6 py-3 text-sm sm:text-base lg:text-lg font-medium rounded-lg text-center transition-all duration-200 hover:scale-105 no-underline"
              style={{ backgroundColor: 'var(--accent)', color: 'white' }}
            >
              Access Dashboard
            </Link>
            <Link
              to="/contact"
              className="w-full sm:w-auto px-6 py-3 text-sm sm:text-base lg:text-lg font-medium rounded-lg text-center transition-all duration-200 hover:scale-105 no-underline"
              style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
            >
              Request Demo
            </Link>
          </div>
          <div className="flex flex-wrap justify-center gap-2 sm:gap-4">
            <Badge variant="primary">Real-time Tracking</Badge>
            <Badge variant="success">Automated Reordering</Badge>
            <Badge variant="info">Multi-location Support</Badge>
            <Badge variant="warning">Advanced Analytics</Badge>
          </div>
        </div>

        {/* Core Features */}
        <div className="mb-12 sm:mb-16">
          <h2
            className="text-2xl sm:text-3xl font-bold text-center mb-8 sm:mb-12"
            style={{ color: 'var(--text-primary)' }}
          >
            Core Features
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            <div
              className="rounded-xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all hover:scale-105 border"
              style={{
                backgroundColor: 'var(--bg-primary)',
                borderColor: 'var(--border)',
                boxShadow: 'var(--shadow)',
              }}
            >
              <h3
                className="text-lg sm:text-xl font-semibold mb-3"
                style={{ color: 'var(--text-primary)' }}
              >
                📦 Inventory Tracking
              </h3>
              <p
                style={{ color: 'var(--text-secondary)' }}
                className="text-sm sm:text-base"
              >
                Real-time stock monitoring, barcode scanning, and automated
                inventory updates across all warehouse locations.
              </p>
            </div>
            <div
              className="rounded-xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all hover:scale-105 border"
              style={{
                backgroundColor: 'var(--bg-primary)',
                borderColor: 'var(--border)',
                boxShadow: 'var(--shadow)',
              }}
            >
              <h3
                className="text-lg sm:text-xl font-semibold mb-3"
                style={{ color: 'var(--text-primary)' }}
              >
                🚚 Order Management
              </h3>
              <p
                style={{ color: 'var(--text-secondary)' }}
                className="text-sm sm:text-base"
              >
                Streamlined order processing, pick list generation, and shipping
                integration for efficient fulfillment operations.
              </p>
            </div>
            <div
              className="rounded-xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all hover:scale-105 border"
              style={{
                backgroundColor: 'var(--bg-primary)',
                borderColor: 'var(--border)',
                boxShadow: 'var(--shadow)',
              }}
            >
              <h3
                className="text-lg sm:text-xl font-semibold mb-3"
                style={{ color: 'var(--text-primary)' }}
              >
                🏭 Warehouse Operations
              </h3>
              <p
                style={{ color: 'var(--text-secondary)' }}
                className="text-sm sm:text-base"
              >
                Optimize storage layouts, manage receiving and putaway
                processes, and track worker productivity.
              </p>
            </div>
            <div
              className="rounded-xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all hover:scale-105 border"
              style={{
                backgroundColor: 'var(--bg-primary)',
                borderColor: 'var(--border)',
                boxShadow: 'var(--shadow)',
              }}
            >
              <h3
                className="text-lg sm:text-xl font-semibold mb-3"
                style={{ color: 'var(--text-primary)' }}
              >
                📊 Analytics & Reporting
              </h3>
              <p
                style={{ color: 'var(--text-secondary)' }}
                className="text-sm sm:text-base"
              >
                Comprehensive dashboards, inventory reports, and performance
                metrics to drive data-driven decisions.
              </p>
            </div>
            <div
              className="rounded-xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all hover:scale-105 border"
              style={{
                backgroundColor: 'var(--bg-primary)',
                borderColor: 'var(--border)',
                boxShadow: 'var(--shadow)',
              }}
            >
              <h3
                className="text-lg sm:text-xl font-semibold mb-3"
                style={{ color: 'var(--text-primary)' }}
              >
                🔄 Automated Reordering
              </h3>
              <p
                style={{ color: 'var(--text-secondary)' }}
                className="text-sm sm:text-base"
              >
                Smart reorder points, automatic purchase order generation, and
                supplier management for optimal stock levels.
              </p>
            </div>
            <div
              className="rounded-xl p-4 sm:p-6 shadow-lg hover:shadow-xl transition-all hover:scale-105 border"
              style={{
                backgroundColor: 'var(--bg-primary)',
                borderColor: 'var(--border)',
                boxShadow: 'var(--shadow)',
              }}
            >
              <h3
                className="text-lg sm:text-xl font-semibold mb-3"
                style={{ color: 'var(--text-primary)' }}
              >
                🌐 Multi-location Support
              </h3>
              <p
                style={{ color: 'var(--text-secondary)' }}
                className="text-sm sm:text-base"
              >
                Manage multiple warehouses, transfer inventory between
                locations, and maintain centralized control.
              </p>
            </div>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="mb-12 sm:mb-16">
          <Typography variant="h2" align="center" className="mb-8 sm:mb-12">
            Why Choose Sudo IMS?
          </Typography>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 max-w-4xl mx-auto">
            <Card className="text-center" padding="lg">
              <h3
                className="text-lg sm:text-xl font-semibold mb-4"
                style={{ color: 'var(--text-primary)' }}
              >
                💰 Cost Reduction
              </h3>
              <Typography variant="body1" color="secondary">
                Reduce carrying costs, minimize stockouts, and optimize storage
                space utilization by up to 30%.
              </Typography>
            </Card>
            <Card className="text-center" padding="lg">
              <h3
                className="text-lg sm:text-xl font-semibold mb-4"
                style={{ color: 'var(--text-primary)' }}
              >
                ⚡ Improved Efficiency
              </h3>
              <Typography variant="body1" color="secondary">
                Streamline operations with automated workflows, reducing manual
                errors and increasing processing speed.
              </Typography>
            </Card>
            <Card className="text-center" padding="lg">
              <h3
                className="text-lg sm:text-xl font-semibold mb-4"
                style={{ color: 'var(--text-primary)' }}
              >
                👁 Real-time Visibility
              </h3>
              <Typography variant="body1" color="secondary">
                Get complete visibility into your inventory levels, movements,
                and performance across all locations.
              </Typography>
            </Card>
            <Card className="text-center" padding="lg">
              <h3
                className="text-lg sm:text-xl font-semibold mb-4"
                style={{ color: 'var(--text-primary)' }}
              >
                📈 Scalable Solution
              </h3>
              <Typography variant="body1" color="secondary">
                Grow your business with confidence using our scalable platform
                that adapts to your expanding needs.
              </Typography>
            </Card>
          </div>
        </div>

        {/* Newsletter Section */}
        {/* <div className="mb-12 sm:mb-16">
          <Newsletter
            title="Stay Updated with Tech Insights"
            description="Join our newsletter to receive the latest updates on emerging technologies, development best practices, and industry insights."
            source="homepage"
            variant="default"
          />
        </div> */}

        {/* Call to Action */}
        <div className="text-center">
          <Card className="max-w-2xl mx-auto" padding="lg">
            <Typography variant="h3" align="center" className="mb-4">
              Ready to Optimize Your Inventory?
            </Typography>
            <Typography
              variant="body1"
              color="secondary"
              className="mb-6"
              align="center"
            >
              Join thousands of businesses using Sudo IMS to streamline their
              warehouse operations and boost efficiency. Start your free trial
              today.
            </Typography>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link
                to="/dashboard"
                className="w-full sm:w-auto px-6 py-3 text-sm sm:text-base lg:text-lg font-medium rounded-lg text-center transition-all duration-200 hover:scale-105 no-underline"
                style={{ backgroundColor: 'var(--accent)', color: 'white' }}
              >
                Start Free Trial
              </Link>
              <Link
                to="/contact"
                className="w-full sm:w-auto px-6 py-3 text-sm sm:text-base lg:text-lg font-medium rounded-lg text-center transition-all duration-200 hover:scale-105 no-underline"
                style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
              >
                Schedule Demo
              </Link>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Home;
